import React from 'react'
import { Link } from 'react-router-dom'
import { Plus } from 'lucide-react'

import { useTranslation } from '@/features/locale/LocaleProvider'
import { nutrimaticLabels } from '@/nutrimatic'
import { LiquidityMobileTokenPickerSheet } from '@/mobile/token-picker/LiquidityMobileTokenPickerSheet'
import { PairState } from '../../../../data/Reserves'
import type { ImportPoolControllerResult } from '../../../../features/liquidity/useImportPoolController'
import { ImportPoolField } from '../../../../features/liquidity/useImportPoolController'
import { useAppWeb3 } from '../../../../hooks'
import { ChainId } from '../../../../sdk'
import { MinimalPositionCard } from '../../../../components/PositionCard'
import type { WidgetRouteIntent } from '../../../navigation/widgetRouteIntent'
import { MobileSwapScreenHeader } from '../../../swap/nutrimatic/mobile/SwapNutrimaticMobileSimpleViews'
import { NutrimaticMobileTokenSelectLeg } from './NutrimaticMobileCurrencyLeg'
import {
  liquidityNutrimaticTabletHeaderProps,
  useLiquidityHubTabletBack,
} from '../useLiquidityHubTabletBack'

type NutrimaticVariant = 'mobile' | 'desktop-tablet'

export function MobileImportPoolRateHero({
  currency0,
  currency1,
}: {
  currency0: ImportPoolControllerResult['currency0']
  currency1: ImportPoolControllerResult['currency1']
}) {
  if (!currency0 || !currency1) {
    return (
      <p className="nm-mobile-simple__rate-hero nm-mobile-simple__rate-hero--idle">
        {nutrimaticLabels.mobileSimple.rateIdle}
      </p>
    )
  }

  return (
    <p className="nm-mobile-simple__rate-hero">
      {currency0.symbol} / {currency1.symbol}
    </p>
  )
}

export function ImportPoolResultNutrimatic({
  account,
  currency0,
  currency1,
  pairState,
  hasPosition,
  pair,
  validPairNoLiquidity,
  addLiquidityCurrencyIds,
  resolveRoute,
}: {
  account: string | undefined
  currency0: ImportPoolControllerResult['currency0']
  currency1: ImportPoolControllerResult['currency1']
  pairState: PairState
  hasPosition: boolean
  pair: ImportPoolControllerResult['pair']
  validPairNoLiquidity: boolean
  addLiquidityCurrencyIds: ImportPoolControllerResult['addLiquidityCurrencyIds']
  resolveRoute: (intent: WidgetRouteIntent) => string
}) {
  const t = useTranslation()

  if (!currency0 || !currency1) {
    return (
      <div className="nm-mobile-simple__review-card nm-mobile-simple__prose nm-mobile-simple__prose--muted">
        {!account
          ? nutrimaticLabels.mobileSimple.importConnectWallet
          : nutrimaticLabels.mobileSimple.importSelectToken}
      </div>
    )
  }

  if (pairState === PairState.LOADING) {
    return (
      <div className="nm-mobile-simple__review-card nm-mobile-simple__prose nm-mobile-simple__prose--muted">{t.common.loading}</div>
    )
  }

  if (pairState === PairState.INVALID) {
    return (
      <div className="nm-mobile-simple__review-card nm-mobile-simple__prose nm-mobile-simple__prose--muted">{t.swapUi.invalidPair}</div>
    )
  }

  if (pairState === PairState.EXISTS) {
    if (hasPosition && pair) {
      return (
        <>
          <div className="rounded-xl bg-primary/10 p-3 text-center nm-mobile-simple__prose font-medium text-primary">
            {nutrimaticLabels.mobileSimple.poolFound}
          </div>
          <MinimalPositionCard pair={pair} border="1px solid color-mix(in srgb, var(--border) 80%, transparent)" />
        </>
      )
    }

    return (
      <div className="nm-mobile-simple__review-card nm-mobile-simple__prose">
        {nutrimaticLabels.mobileSimple.importNoPosition}{' '}
        {addLiquidityCurrencyIds ? (
          <Link
            to={resolveRoute({ type: 'add', ...addLiquidityCurrencyIds })}
            className="font-medium text-primary hover:underline"
          >
            {nutrimaticLabels.mobileSimple.addLiquidityLink}
          </Link>
        ) : null}
      </div>
    )
  }

  if (validPairNoLiquidity) {
    return (
      <div className="nm-mobile-simple__review-card nm-mobile-simple__prose">
        {nutrimaticLabels.mobileSimple.importNoPool}{' '}
        {addLiquidityCurrencyIds ? (
          <Link
            to={resolveRoute({ type: 'add', ...addLiquidityCurrencyIds })}
            className="font-medium text-primary hover:underline"
          >
            {nutrimaticLabels.mobileSimple.createPoolLink}
          </Link>
        ) : null}
      </div>
    )
  }

  return null
}

export function LiquidityNutrimaticMobileImportFormView({
  controller,
  resolveRoute,
  variant = 'mobile',
}: {
  controller: ImportPoolControllerResult
  resolveRoute: (intent: WidgetRouteIntent) => string
  variant?: NutrimaticVariant
}) {
  const isDesktopTablet = variant === 'desktop-tablet'
  const backToPositions = useLiquidityHubTabletBack()
  const {
    account,
    addLiquidityCurrencyIds,
    currency0,
    currency1,
    hasPosition,
    openSearchForField,
    pair,
    pairState,
    validPairNoLiquidity,
  } = controller

  return (
    <div className="nm-mobile-simple__convert-stage flex min-h-0 flex-1 flex-col overflow-hidden">
      <MobileSwapScreenHeader
        title={nutrimaticLabels.mobileSimple.importPoolTitle}
        {...liquidityNutrimaticTabletHeaderProps(variant, backToPositions)}
      />

      <div className="nm-mobile-simple__body nm-mobile-simple__body--trade" data-mobile-scroll-container>
        <div className="nm-mobile-simple__trade-stack">
          <NutrimaticMobileTokenSelectLeg
            currency={currency0}
            onOpenTokenPicker={() => openSearchForField(ImportPoolField.TOKEN0)}
            position="from"
          />

          <NutrimaticMobileTokenSelectLeg
            currency={currency1}
            onOpenTokenPicker={() => openSearchForField(ImportPoolField.TOKEN1)}
            position="to"
          />

          <div className="nm-mobile-simple__swap-bridge" aria-hidden>
            <span className="nm-mobile-simple__swap-bridge-icon">
              <Plus className="size-4" />
            </span>
          </div>
        </div>

        <div className="mx-4 mt-4 space-y-3 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <ImportPoolResultNutrimatic
            account={account}
            currency0={currency0}
            currency1={currency1}
            pairState={pairState}
            hasPosition={hasPosition}
            pair={pair}
            validPairNoLiquidity={validPairNoLiquidity}
            addLiquidityCurrencyIds={addLiquidityCurrencyIds}
            resolveRoute={resolveRoute}
          />
        </div>
      </div>
    </div>
  )
}

/** Flujo móvil completo con picker fullscreen. */
export function LiquidityNutrimaticMobileImportView({
  controller,
  resolveRoute,
}: {
  controller: ImportPoolControllerResult
  resolveRoute: (intent: WidgetRouteIntent) => string
}) {
  const t = useTranslation()
  const {
    activeField,
    currency0,
    currency1,
    handleCurrencySelect,
    handleSearchDismiss,
    showSearch,
  } = controller
  const { chainId } = useAppWeb3()

  return (
    <>
      <LiquidityMobileTokenPickerSheet
        open={showSearch}
        onClose={handleSearchDismiss}
        chainId={chainId ?? ChainId.INFINITE_CREATIVE}
        title={activeField === ImportPoolField.TOKEN0 ? t.common.selectToken1 : t.common.selectToken2}
        excludeCurrency={(activeField === ImportPoolField.TOKEN0 ? currency1 : currency0) ?? undefined}
        onSelect={handleCurrencySelect}
      />

      <LiquidityNutrimaticMobileImportFormView
        controller={controller}
        resolveRoute={resolveRoute}
        variant="mobile"
      />
    </>
  )
}
